import { queryOptions } from "@tanstack/react-query";

import { api, mBaseURL } from "@/config/api";
import { unwrapApiError } from "@/lib/error";
import { createQueryString } from "@/lib/utils";
import { APIResponseSuccessSchema } from "@/schema/api";
import {
  ConsumerResearchOverviewStatsDataSchema,
  ConsumerResearchOverviewSentimentTrendDataSchema,
  ConsumerResearchOverviewWeeklyVolumeDataSchema,
  ConsumerResearchOverviewChannelMixDataSchema,
  ConsumerResearchOverviewTopConsumerThemesDataSchema,
  ConsumerResearchCollectionReviewIntelligenceDataSchema,
  ConsumerResearchCollectionPerformanceDataSchema,
  SentimentByChannelDataSchema,
  GrowthMomentumDataSchema,
  DiscussionTopicsDataSchema,
  ChannelHealthDataSchema,
  FeedDataSchema,
} from "@/schema/brand-os/ai-consumer-research";

export type FeedParams = {
  page?: number;
};

export const ConsumerResearchApiFactory = {
  Endpoint: mBaseURL + "/api/brand/consumer",

  Keys: {
    OverviewStats: () => ["consumer-research", "overview", "stats"],
    OverviewSentimentTrend: () => ["consumer-research", "overview", "sentiment-trend"],
    OverviewWeeklyVolume: () => ["consumer-research", "overview", "weekly-volume"],
    OverviewChannelMix: () => ["consumer-research", "overview", "channel-mix"],
    OverviewTopConsumerThemes: () => ["consumer-research", "overview", "top-consumer-themes"],
    CollectionReviewIntelligence: () => ["consumer-research", "collection", "review-intelligence"],
    CollectionPerformance: () => ["consumer-research", "collection", "performance"],
    SentimentByChannel: () => ["consumer-research", "channel", "sentiment-by-channel"],
    GrowthMomentum: () => ["consumer-research", "channel", "growth-momentum"],
    DiscussionTopics: () => ["consumer-research", "channel", "discussion-topics"],
    ChannelHealth: () => ["consumer-research", "channel", "channel-health"],
    Feed: (params: FeedParams) => ["consumer-research", "feed", createQueryString({ page: params.page ?? 1 })],
  },

  Schemas: {
    Response: {
      OverviewStats: APIResponseSuccessSchema.extend({
        data: ConsumerResearchOverviewStatsDataSchema,
      }),
      OverviewSentimentTrend: APIResponseSuccessSchema.extend({
        data: ConsumerResearchOverviewSentimentTrendDataSchema,
      }),
      OverviewWeeklyVolume: APIResponseSuccessSchema.extend({
        data: ConsumerResearchOverviewWeeklyVolumeDataSchema,
      }),
      OverviewChannelMix: APIResponseSuccessSchema.extend({
        data: ConsumerResearchOverviewChannelMixDataSchema,
      }),
      OverviewTopConsumerThemes: APIResponseSuccessSchema.extend({
        data: ConsumerResearchOverviewTopConsumerThemesDataSchema,
      }),
      CollectionReviewIntelligence: APIResponseSuccessSchema.extend({
        data: ConsumerResearchCollectionReviewIntelligenceDataSchema,
      }),
      CollectionPerformance: APIResponseSuccessSchema.extend({
        data: ConsumerResearchCollectionPerformanceDataSchema,
      }),
      SentimentByChannel: APIResponseSuccessSchema.extend({
        data: SentimentByChannelDataSchema,
      }),
      GrowthMomentum: APIResponseSuccessSchema.extend({
        data: GrowthMomentumDataSchema,
      }),
      DiscussionTopics: APIResponseSuccessSchema.extend({
        data: DiscussionTopicsDataSchema,
      }),
      ChannelHealth: APIResponseSuccessSchema.extend({
        data: ChannelHealthDataSchema,
      }),
      Feed: APIResponseSuccessSchema.extend({
        data: FeedDataSchema,
      }),
    },
  },

  Apis: {
    GetOverviewStats: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/overview/stats");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.OverviewStats.parse)
        .then((data) => data.data);
    },

    GetOverviewSentimentTrend: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/overview/sentiment-trend");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.OverviewSentimentTrend.parse)
        .then((data) => data.data);
    },

    GetOverviewWeeklyVolume: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/overview/weekly-volume");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.OverviewWeeklyVolume.parse)
        .then((data) => data.data);
    },

    GetOverviewChannelMix: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/overview/channel-mix");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.OverviewChannelMix.parse)
        .then((data) => data.data);
    },

    GetOverviewTopConsumerThemes: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/overview/top-consumer-themes");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.OverviewTopConsumerThemes.parse)
        .then((data) => data.data);
    },

    GetCollectionReviewIntelligence: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/collection/review-intelligence");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.CollectionReviewIntelligence.parse)
        .then((data) => data.data);
    },

    GetCollectionPerformance: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/collection/performance");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.CollectionPerformance.parse)
        .then((data) => data.data);
    },

    GetSentimentByChannel: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/channel/sentiment-by-channel");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.SentimentByChannel.parse)
        .then((res) => res.data);
    },

    GetGrowthMomentum: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/channel/growth-momentum");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.GrowthMomentum.parse)
        .then((res) => res.data);
    },

    GetDiscussionTopics: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/channel/discussion-topics");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.DiscussionTopics.parse)
        .then((res) => res.data);
    },

    GetChannelHealth: async () => {
      const response = await api(ConsumerResearchApiFactory.Endpoint + "/channel/channel-health");

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.ChannelHealth.parse)
        .then((res) => res.data);
    },

    GetFeed: async (params: FeedParams = {}) => {
      const query = createQueryString({ page: params.page ?? 1 });
      const response = await api(`${ConsumerResearchApiFactory.Endpoint}/feed/?${query}`);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(ConsumerResearchApiFactory.Schemas.Response.Feed.parse)
        .then((res) => res.data);
    },
  },

  Queries: {
    OverviewStats: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.OverviewStats(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetOverviewStats(),
      });
    },
    OverviewSentimentTrend: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.OverviewSentimentTrend(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetOverviewSentimentTrend(),
      });
    },
    OverviewWeeklyVolume: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.OverviewWeeklyVolume(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetOverviewWeeklyVolume(),
      });
    },
    OverviewChannelMix: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.OverviewChannelMix(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetOverviewChannelMix(),
      });
    },
    OverviewTopConsumerThemes: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.OverviewTopConsumerThemes(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetOverviewTopConsumerThemes(),
      });
    },
    CollectionReviewIntelligence: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.CollectionReviewIntelligence(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetCollectionReviewIntelligence(),
      });
    },
    CollectionPerformance: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.CollectionPerformance(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetCollectionPerformance(),
      });
    },
    SentimentByChannel: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.SentimentByChannel(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetSentimentByChannel(),
      });
    },
    GrowthMomentum: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.GrowthMomentum(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetGrowthMomentum(),
      });
    },
    DiscussionTopics: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.DiscussionTopics(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetDiscussionTopics(),
      });
    },
    ChannelHealth: () => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.ChannelHealth(),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetChannelHealth(),
      });
    },
    Feed: (params: FeedParams = {}) => {
      return queryOptions({
        queryKey: ConsumerResearchApiFactory.Keys.Feed(params),
        queryFn: () => ConsumerResearchApiFactory.Apis.GetFeed(params),
      });
    },
  },
};
